import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiZap, FiCheckCircle, FiAlertTriangle, FiRefreshCw, FiChevronDown,
  FiChevronUp, FiBox, FiDollarSign, FiSearch, FiX, FiDownload,
  FiTrendingUp, FiShield, FiLayers, FiTarget, FiCheck, FiPlus,
  FiInfo, FiFilter, FiStar, FiAward, FiCpu, FiServer, FiClock,
  FiGitBranch, FiMaximize2, FiMinimize, FiCrosshair, FiZoomIn, FiZoomOut
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';
import { getAllComponents } from '../../../data/aiOntology';

// ==================== LLM PROVIDERS DATA ====================

const LLM_PROVIDERS = [
  {
    id: 'deepseek',
    name: 'DeepSeek',
    icon: '🐋',
    models: [
      { id: 'deepseek-chat', name: 'DeepSeek Chat', contextWindow: '64K', costPer1K: { input: 0.14, output: 0.28 }, quality: 4.5, speed: 4.2, specialties: ['Reasoning', 'Code', 'Math'] },
      { id: 'deepseek-coder', name: 'DeepSeek Coder', contextWindow: '128K', costPer1K: { input: 0.14, output: 0.28 }, quality: 4.6, speed: 4.3, specialties: ['Code Generation', 'Debugging'] },
      { id: 'deepseek-v3', name: 'DeepSeek V3', contextWindow: '128K', costPer1K: { input: 0.27, output: 1.10 }, quality: 4.8, speed: 4.0, specialties: ['General', 'Reasoning'] }
    ],
    costTier: 'budget',
    apiSupport: true,
    openSource: false
  },
  {
    id: 'openai',
    name: 'OpenAI',
    icon: '🤖',
    models: [
      { id: 'gpt-4o', name: 'GPT-4o', contextWindow: '128K', costPer1K: { input: 2.50, output: 10.00 }, quality: 4.9, speed: 4.5, specialties: ['Multimodal', 'General', 'Vision'] },
      { id: 'gpt-4o-mini', name: 'GPT-4o Mini', contextWindow: '128K', costPer1K: { input: 0.15, output: 0.60 }, quality: 4.3, speed: 4.8, specialties: ['Fast', 'Cost-effective'] },
      { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', contextWindow: '128K', costPer1K: { input: 10.00, output: 30.00 }, quality: 4.8, speed: 4.0, specialties: ['Complex Reasoning'] },
      { id: 'o1-preview', name: 'O1 Preview', contextWindow: '128K', costPer1K: { input: 15.00, output: 60.00 }, quality: 5.0, speed: 3.0, specialties: ['Deep Reasoning', 'Math', 'Science'] }
    ],
    costTier: 'premium',
    apiSupport: true,
    openSource: false
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    icon: '🧠',
    models: [
      { id: 'claude-3-opus', name: 'Claude 3 Opus', contextWindow: '200K', costPer1K: { input: 15.00, output: 75.00 }, quality: 4.9, speed: 3.5, specialties: ['Analysis', 'Writing', 'Reasoning'] },
      { id: 'claude-3-sonnet', name: 'Claude 3 Sonnet', contextWindow: '200K', costPer1K: { input: 3.00, output: 15.00 }, quality: 4.7, speed: 4.2, specialties: ['Balanced', 'General'] },
      { id: 'claude-3-haiku', name: 'Claude 3 Haiku', contextWindow: '200K', costPer1K: { input: 0.25, output: 1.25 }, quality: 4.2, speed: 4.8, specialties: ['Fast', 'Lightweight'] },
      { id: 'claude-3-5-sonnet', name: 'Claude 3.5 Sonnet', contextWindow: '200K', costPer1K: { input: 3.00, output: 15.00 }, quality: 4.8, speed: 4.3, specialties: ['Coding', 'Reasoning'] }
    ],
    costTier: 'premium',
    apiSupport: true,
    openSource: false
  },
  {
    id: 'kimi',
    name: 'Kimi (Moonshot)',
    icon: '🌙',
    models: [
      { id: 'kimi-k2', name: 'Kimi K2', contextWindow: '128K', costPer1K: { input: 0.60, output: 2.50 }, quality: 4.6, speed: 4.4, specialties: ['Long Context', 'Chinese/English'] },
      { id: 'kimi-latest', name: 'Kimi Latest', contextWindow: '256K', costPer1K: { input: 1.00, output: 4.00 }, quality: 4.7, speed: 4.3, specialties: ['Long Documents', 'Analysis'] }
    ],
    costTier: 'mid',
    apiSupport: true,
    openSource: false
  },
  {
    id: 'meta',
    name: 'Meta (Llama)',
    icon: '🦙',
    models: [
      { id: 'llama-3-70b', name: 'Llama 3 70B', contextWindow: '8K', costPer1K: { input: 0.90, output: 0.90 }, quality: 4.4, speed: 4.0, specialties: ['Open Source', 'Customizable'] },
      { id: 'llama-3-405b', name: 'Llama 3 405B', contextWindow: '8K', costPer1K: { input: 3.00, output: 3.00 }, quality: 4.7, speed: 3.5, specialties: ['Large Scale', 'Reasoning'] },
      { id: 'llama-3-1-70b', name: 'Llama 3.1 70B', contextWindow: '128K', costPer1K: { input: 0.90, output: 0.90 }, quality: 4.5, speed: 4.0, specialties: ['Multilingual', 'Open'] }
    ],
    costTier: 'open-source',
    apiSupport: true,
    openSource: true
  },
  {
    id: 'google',
    name: 'Google (Gemini)',
    icon: '💎',
    models: [
      { id: 'gemini-1-5-pro', name: 'Gemini 1.5 Pro', contextWindow: '1M', costPer1K: { input: 3.50, output: 10.50 }, quality: 4.8, speed: 4.0, specialties: ['Long Context', 'Multimodal'] },
      { id: 'gemini-1-5-flash', name: 'Gemini 1.5 Flash', contextWindow: '1M', costPer1K: { input: 0.075, output: 0.30 }, quality: 4.3, speed: 4.9, specialties: ['Fast', 'Cost-effective'] },
      { id: 'gemini-2-0', name: 'Gemini 2.0', contextWindow: '1M', costPer1K: { input: 4.00, output: 12.00 }, quality: 4.9, speed: 4.2, specialties: ['Advanced', 'Multimodal'] }
    ],
    costTier: 'premium',
    apiSupport: true,
    openSource: false
  },
  {
    id: 'mistral',
    name: 'Mistral AI',
    icon: '🌪️',
    models: [
      { id: 'mistral-large', name: 'Mistral Large', contextWindow: '128K', costPer1K: { input: 2.00, output: 6.00 }, quality: 4.6, speed: 4.3, specialties: ['European', 'Multilingual'] },
      { id: 'mistral-small', name: 'Mistral Small', contextWindow: '32K', costPer1K: { input: 0.20, output: 0.60 }, quality: 4.2, speed: 4.7, specialties: ['Efficient', 'Lightweight'] },
      { id: 'mixtral-8x7b', name: 'Mixtral 8x7B', contextWindow: '32K', costPer1K: { input: 0.60, output: 0.60 }, quality: 4.4, speed: 4.1, specialties: ['Open Source', 'MoE'] }
    ],
    costTier: 'mid',
    apiSupport: true,
    openSource: true
  },
  {
    id: 'cohere',
    name: 'Cohere',
    icon: '🔗',
    models: [
      { id: 'command-r-plus', name: 'Command R+', contextWindow: '128K', costPer1K: { input: 3.00, output: 15.00 }, quality: 4.5, speed: 4.0, specialties: ['Enterprise', 'RAG'] },
      { id: 'command-r', name: 'Command R', contextWindow: '128K', costPer1K: { input: 0.50, output: 1.50 }, quality: 4.3, speed: 4.2, specialties: ['RAG', 'Enterprise'] }
    ],
    costTier: 'mid',
    apiSupport: true,
    openSource: false
  },
  {
    id: 'qwen',
    name: 'Qwen (Alibaba)',
    icon: '🐉',
    models: [
      { id: 'qwen-max', name: 'Qwen Max', contextWindow: '32K', costPer1K: { input: 1.20, output: 1.20 }, quality: 4.5, speed: 4.2, specialties: ['Chinese', 'Multilingual'] },
      { id: 'qwen-72b', name: 'Qwen 72B', contextWindow: '32K', costPer1K: { input: 0.90, output: 0.90 }, quality: 4.3, speed: 4.0, specialties: ['Open Source', 'Chinese'] }
    ],
    costTier: 'mid',
    apiSupport: true,
    openSource: true
  },
  {
    id: 'baidu',
    name: 'Baidu (ERNIE)',
    icon: '🐻',
    models: [
      { id: 'ernie-4', name: 'ERNIE 4.0', contextWindow: '8K', costPer1K: { input: 1.50, output: 1.50 }, quality: 4.4, speed: 4.0, specialties: ['Chinese', 'Enterprise'] },
      { id: 'ernie-speed', name: 'ERNIE Speed', contextWindow: '8K', costPer1K: { input: 0.10, output: 0.10 }, quality: 4.0, speed: 4.8, specialties: ['Fast', 'Cost-effective'] }
    ],
    costTier: 'mid',
    apiSupport: true,
    openSource: false
  },
  {
    id: 'xai',
    name: 'xAI (Grok)',
    icon: '🚀',
    models: [
      { id: 'grok-2', name: 'Grok 2', contextWindow: '128K', costPer1K: { input: 2.00, output: 8.00 }, quality: 4.6, speed: 4.0, specialties: ['Real-time', 'Reasoning'] },
      { id: 'grok-2-mini', name: 'Grok 2 Mini', contextWindow: '128K', costPer1K: { input: 0.50, output: 2.00 }, quality: 4.2, speed: 4.6, specialties: ['Fast', 'Cost-effective'] }
    ],
    costTier: 'premium',
    apiSupport: true,
    openSource: false
  }
];

// ==================== CONSTRAINT OPTIONS ====================

const CONSTRAINT_OPTIONS = {
  cost: {
    label: 'Cost',
    icon: '💰',
    options: ['Low Cost', 'Medium Cost', 'Premium']
  },
  time: {
    label: 'Time',
    icon: '⏱️',
    options: ['Fast Deployment', 'Balanced', 'Extended']
  },
  scope: {
    label: 'Scope',
    icon: '🎯',
    options: ['Minimal', 'Standard', 'Comprehensive']
  },
  quality: {
    label: 'Quality',
    icon: '⭐',
    options: ['Good', 'Better', 'Best']
  },
  security: {
    label: 'Security',
    icon: '🛡️',
    options: ['Standard', 'Enhanced', 'Critical']
  },
  scalability: {
    label: 'Scalability',
    icon: '📈',
    options: ['Startup Scale', 'SMB Scale', 'Enterprise Scale']
  },
  compliance: {
    label: 'Compliance',
    icon: '⚖️',
    options: ['Basic', 'SOC2', 'HIPAA/GDPR']
  },
  openSource: {
    label: 'Open Source Preference',
    icon: '🔓',
    options: ['Open Source Only', 'Mixed', 'Commercial OK']
  }
};

// ==================== ALTERNATIVE SOLUTION GENERATOR ====================

const generateAlternativeSolution = (constraints, selectedLLMs, currentComponents) => {
  const alternatives = [];
  
  // For each selected LLM provider
  selectedLLMs.forEach(providerId => {
    const provider = LLM_PROVIDERS.find(p => p.id === providerId);
    if (!provider) return;
    
    provider.models.forEach(model => {
      // Score model against constraints
      const score = scoreModelAgainstConstraints(model, provider, constraints);
      
      if (score >= 60) {
        alternatives.push({
          id: `alt-${provider.id}-${model.id}`,
          providerName: provider.name,
          providerIcon: provider.icon,
          modelName: model.name,
          modelId: model.id,
          contextWindow: model.contextWindow,
          costPer1K: model.costPer1K,
          quality: model.quality,
          speed: model.speed,
          specialties: model.specialties,
          openSource: provider.openSource,
          costTier: provider.costTier,
          score,
          estimatedMonthlyCost: calculateEstimatedCost(model, constraints),
          recommendedComponents: getRecommendedComponents(model, provider, constraints),
          benefits: getModelBenefits(model, provider, constraints),
          tradeoffs: getModelTradeoffs(model, constraints)
        });
      }
    });
  });
  
  // Sort by score
  alternatives.sort((a, b) => b.score - a.score);
  
  return alternatives;
};

const scoreModelAgainstConstraints = (model, provider, constraints) => {
  let score = 70; // Base score
  
  // Cost constraint
  if (constraints.cost === 'Low Cost') {
    if (provider.costTier === 'budget' || provider.costTier === 'open-source') score += 15;
    else if (provider.costTier === 'premium') score -= 20;
  } else if (constraints.cost === 'Premium') {
    if (provider.costTier === 'premium') score += 10;
    else score -= 5;
  }
  
  // Time constraint
  if (constraints.time === 'Fast Deployment') {
    if (model.speed >= 4.5) score += 10;
    else if (model.speed < 3.5) score -= 15;
  } else if (constraints.time === 'Extended') {
    if (model.speed < 4.0) score += 5;
  }
  
  // Quality constraint
  if (constraints.quality === 'Best') {
    if (model.quality >= 4.8) score += 15;
    else if (model.quality < 4.5) score -= 15;
  } else if (constraints.quality === 'Good') {
    if (model.quality >= 4.0 && model.speed >= 4.5) score += 10;
  }
  
  // Open source constraint
  if (constraints.openSource === 'Open Source Only') {
    if (provider.openSource) score += 10;
    else score -= 25;
  } else if (constraints.openSource === 'Commercial OK') {
    if (!provider.openSource) score += 5;
  }
  
  // Security constraint
  if (constraints.security === 'Critical') {
    if (provider.name === 'Anthropic' || provider.name === 'OpenAI') score += 10;
    else score -= 5;
  }
  
  return Math.min(Math.max(score, 0), 100);
};

const calculateEstimatedCost = (model, constraints) => {
  const requestsPerMonth = constraints.scope === 'Comprehensive' ? 1000000 : 
                            constraints.scope === 'Standard' ? 500000 : 100000;
  const avgTokensPerRequest = 1000;
  
  const inputCost = (model.costPer1K.input / 1000) * requestsPerMonth * avgTokensPerRequest * 0.7;
  const outputCost = (model.costPer1K.output / 1000) * requestsPerMonth * avgTokensPerRequest * 0.3;
  
  return inputCost + outputCost;
};

const getRecommendedComponents = (model, provider, constraints) => {
  const components = [];
  const allComponents = getAllComponents();
  
  // Add LLM-related components
  components.push(allComponents.find(c => c.name === 'Transformer'));
  components.push(allComponents.find(c => c.name === 'Tokenization (BPE)'));
  components.push(allComponents.find(c => c.name === 'Prompt Templates'));
  components.push(allComponents.find(c => c.name === 'Embedding Generation'));
  
  if (constraints.security === 'Critical') {
    components.push(allComponents.find(c => c.name === 'Prompt Injection Firewall'));
    components.push(allComponents.find(c => c.name === 'Guardrails'));
    components.push(allComponents.find(c => c.name === 'Safety Classifiers'));
  }
  
  if (constraints.scalability === 'Enterprise Scale') {
    components.push(allComponents.find(c => c.name === 'Kubernetes'));
    components.push(allComponents.find(c => c.name === 'Load Balancer'));
    components.push(allComponents.find(c => c.name === 'Redis'));
  }
  
  if (constraints.compliance === 'HIPAA/GDPR') {
    components.push(allComponents.find(c => c.name === 'Audit Logs (SIEM)'));
    components.push(allComponents.find(c => c.name === 'Data Encryption (TLS/AES)'));
  }
  
  return components.filter(Boolean);
};

const getModelBenefits = (model, provider, constraints) => {
  const benefits = [];
  
  if (model.quality >= 4.8) benefits.push('Top-tier quality output');
  if (model.speed >= 4.5) benefits.push('Fast response times');
  if (provider.openSource) benefits.push('Open source - full control');
  if (model.contextWindow.includes('K') && parseInt(model.contextWindow) >= 128) benefits.push(`Large context window (${model.contextWindow})`);
  if (provider.costTier === 'budget' || provider.costTier === 'open-source') benefits.push('Cost-effective');
  if (model.specialties?.length > 0) benefits.push(`Specialized in: ${model.specialties.join(', ')}`);
  
  return benefits;
};

const getModelTradeoffs = (model, constraints) => {
  const tradeoffs = [];
  
  if (model.costPer1K.output > 10) tradeoffs.push('Higher output cost');
  if (model.speed < 3.5) tradeoffs.push('Slower response time');
  if (model.quality < 4.3) tradeoffs.push('Moderate quality');
  if (!model.specialties?.includes('General')) tradeoffs.push('May not be suitable for all tasks');
  
  return tradeoffs;
};

// ==================== MAIN COMPONENT ====================

const AlternativeSolutionsView = () => {
  const { state, dispatch } = useSolution();
  
  const [constraints, setConstraints] = useState({
    cost: 'Medium Cost',
    time: 'Balanced',
    scope: 'Standard',
    quality: 'Better',
    security: 'Standard',
    scalability: 'SMB Scale',
    compliance: 'Basic',
    openSource: 'Mixed'
  });
  
  const [selectedProviders, setSelectedProviders] = useState(['deepseek', 'openai', 'anthropic', 'kimi', 'google', 'meta']);
  const [alternatives, setAlternatives] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [expandedAlternative, setExpandedAlternative] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mainContainerRef = useRef(null);
  
  // Toggle provider selection
  const toggleProvider = useCallback((providerId) => {
    setSelectedProviders(prev => {
      const current = [...prev];
      const index = current.indexOf(providerId);
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(providerId);
      }
      return current;
    });
  }, []);
  
  // Generate alternatives
  const handleGenerate = useCallback(() => {
    const results = generateAlternativeSolution(constraints, selectedProviders, state.selectedComponents);
    setAlternatives(results);
    setShowResults(true);
    setExpandedAlternative(results.length > 0 ? results[0].id : null);
  }, [constraints, selectedProviders, state.selectedComponents]);
  
  // Apply alternative to solution
  const handleMakeCurrent = useCallback((alternative) => {
    // Clear existing selection
    dispatch({ type: 'CLEAR_SELECTION' });
    
    // Add recommended components from alternative
    alternative.recommendedComponents.forEach(comp => {
      if (comp) {
        dispatch({ type: 'ADD_COMPONENT', payload: comp });
      }
    });
    
    // Add existing components that are still relevant
    state.selectedComponents.forEach(comp => {
      dispatch({ type: 'ADD_COMPONENT', payload: comp });
    });
    
    // Create solution with LLM info
    dispatch({
      type: 'CREATE_SOLUTION',
      payload: {
        name: `${alternative.providerName}-${alternative.modelName}`,
        description: `Alternative solution using ${alternative.providerName} ${alternative.modelName}`,
        requirements: {
          ...state.autoArchitectRequirements,
          llmProvider: alternative.providerName,
          llmModel: alternative.modelName,
          constraints: constraints
        }
      }
    });
    
    // Show success
    dispatch({
      type: 'SHOW_TOAST',
      payload: {
        type: 'success',
        message: `${alternative.providerName} ${alternative.modelName} applied as current solution`
      }
    });
  }, [dispatch, state.selectedComponents, constraints]);
  
  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    const element = mainContainerRef.current;
    if (!element) return;
    if (!document.fullscreenElement) {
      element.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);
  
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen]);
  
  return (
    <div 
      ref={mainContainerRef}
      className={`h-full overflow-y-auto ${isFullscreen ? 'bg-white' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}
      style={isFullscreen ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}}
    >
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
            <FiCpu className="w-8 h-8 text-blue-600" />
            Alternative AI Solutions
          </h2>
          <p className="text-gray-600">
            Compare leading LLM providers and derive optimal alternative solutions
          </p>
        </div>
        
        {/* STEP 1: Select LLM Providers */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
          <div className="p-4 bg-blue-50 border-b border-blue-100 flex items-center gap-3">
            <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">1</span>
            <h3 className="text-lg font-bold text-gray-900">Select LLM Providers (Multi-Select)</h3>
            <span className="ml-auto text-sm text-blue-600 font-semibold">{selectedProviders.length} selected</span>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {LLM_PROVIDERS.map(provider => {
                const isSelected = selectedProviders.includes(provider.id);
                return (
                  <button
                    key={provider.id}
                    onClick={() => toggleProvider(provider.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-center relative ${
                      isSelected ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`absolute top-2 right-2 w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <FiCheck className="w-3 h-3 text-white" />}
                    </div>
                    <div className="text-3xl mb-2">{provider.icon}</div>
                    <div className="text-sm font-semibold">{provider.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{provider.models.length} models</div>
                    <div className={`mt-1 px-2 py-0.5 rounded-full text-xs ${
                      provider.costTier === 'premium' ? 'bg-red-100 text-red-700' :
                      provider.costTier === 'budget' ? 'bg-green-100 text-green-700' :
                      provider.costTier === 'open-source' ? 'bg-purple-100 text-purple-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {provider.costTier}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* STEP 2: Constraints */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
          <div className="p-4 bg-green-50 border-b border-green-100 flex items-center gap-3">
            <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">2</span>
            <h3 className="text-lg font-bold text-gray-900">Define Constraints</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(CONSTRAINT_OPTIONS).map(([key, constraint]) => (
                <div key={key}>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {constraint.icon} {constraint.label}
                  </label>
                  <select
                    value={constraints[key]}
                    onChange={(e) => setConstraints(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {constraint.options.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Generate Button */}
        <div className="text-center mb-8">
          <button
            onClick={handleGenerate}
            className="px-10 py-5 rounded-2xl font-bold text-white text-lg bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
          >
            🚀 Generate Alternative Solutions
          </button>
        </div>
        
        {/* Results */}
        {showResults && alternatives.length > 0 && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900">
              {alternatives.length} Alternative Solutions Found
            </h3>
            
            {alternatives.map((alt, index) => (
              <motion.div
                key={alt.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              >
                {/* Header */}
                <button
                  onClick={() => setExpandedAlternative(expandedAlternative === alt.id ? null : alt.id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{alt.providerIcon}</span>
                    <div className="text-left">
                      <div className="font-bold text-gray-900 text-lg">{alt.providerName} - {alt.modelName}</div>
                      <div className="text-xs text-gray-500">
                        Score: {alt.score}% • Quality: {alt.quality}/5 • Speed: {alt.speed}/5
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-semibold text-green-600">
                        ~${alt.estimatedMonthlyCost.toFixed(0)}/mo
                      </div>
                      <div className="text-xs text-gray-500">Context: {alt.contextWindow}</div>
                    </div>
                    {expandedAlternative === alt.id ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                </button>
                
                {/* Expanded Details */}
                <AnimatePresence>
                  {expandedAlternative === alt.id && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-5 border-t border-gray-100 bg-gray-50">
                        {/* Benefits */}
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-700 mb-2">✅ Benefits</h4>
                          <div className="space-y-1">
                            {alt.benefits.map((benefit, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                                <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                {benefit}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Tradeoffs */}
                        {alt.tradeoffs.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-semibold text-gray-700 mb-2">⚠️ Tradeoffs</h4>
                            <div className="space-y-1">
                              {alt.tradeoffs.map((tradeoff, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                                  <FiAlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                                  {tradeoff}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Cost Details */}
                        <div className="mb-4 grid grid-cols-2 gap-4">
                          <div className="p-3 bg-white rounded-lg">
                            <div className="text-xs text-gray-500">Input Cost</div>
                            <div className="font-semibold">${alt.costPer1K.input}/1K tokens</div>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <div className="text-xs text-gray-500">Output Cost</div>
                            <div className="font-semibold">${alt.costPer1K.output}/1K tokens</div>
                          </div>
                        </div>
                        
                        {/* Make Current Button */}
                        <button
                          onClick={() => handleMakeCurrent(alt)}
                          className="w-full px-6 py-4 bg-green-500 text-white rounded-xl hover:bg-green-600 font-bold text-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <FiCheck className="w-5 h-5" />
                          Make Current Solution
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* No results */}
        {showResults && alternatives.length === 0 && (
          <div className="text-center p-8 bg-white rounded-2xl border border-gray-200">
            <div className="text-5xl mb-4">🔍</div>
            <p className="text-gray-600">No alternatives found for current constraints</p>
            <p className="text-sm text-gray-500">Try relaxing some constraints</p>
          </div>
        )}
        
        {/* Fullscreen Button */}
        <button
          onClick={toggleFullscreen}
          className="fixed bottom-6 right-6 p-3 bg-blue-500 text-white rounded-full shadow-xl hover:bg-blue-600 z-40"
          title="Fullscreen (F11)"
        >
          {isFullscreen ? <FiMinimize className="w-5 h-5" /> : <FiMaximize2 className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

export default AlternativeSolutionsView;